import React, { useState, useEffect } from 'react';
import { InformePericial, FaseInforme, PartidaValoracion } from '../../types';
import { informeService } from '../../services/api';
import { FiEdit, FiFileText, FiLoader, FiCpu, FiRefreshCw, FiCheck, FiLayers } from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import { MDXEditor, headingsPlugin, listsPlugin, quotePlugin, thematicBreakPlugin, markdownShortcutPlugin, toolbarPlugin, UndoRedo, BoldItalicUnderlineToggles, BlockTypeSelect } from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import './EncargoForm.css';

interface InformeViewerProps {
    informe?: InformePericial;
    onUpdate: (informe: InformePericial, targetFase?: FaseInforme) => void;
}

type CapituloTab = 'cliente' | 'siniestro' | 'riesgo';

const InformeViewer: React.FC<InformeViewerProps> = ({ informe, onUpdate }) => {
    const [activeTab, setActiveTab] = useState<CapituloTab>('cliente');

    // Estado individual por capítulo
    const [capituloCliente, setCapituloCliente] = useState('');
    const [capituloSiniestro, setCapituloSiniestro] = useState('');
    const [capituloRiesgo, setCapituloRiesgo] = useState('');
    const [capituloCapitales, setCapituloCapitales] = useState('');

    const [tablaValoracion, setTablaValoracion] = useState<PartidaValoracion[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingCapitulo, setLoadingCapitulo] = useState<string | null>(null);
    const [downloadingDocx, setDownloadingDocx] = useState<boolean>(false);
    const [error, setError] = useState('');
    const [generating, setGenerating] = useState(false);

    // Templates state
    const [templates, setTemplates] = useState<any[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | undefined>(undefined);

    useEffect(() => {
        if (informe) {
            setCapituloCliente(informe.capitulo_datos_cliente || '');
            setCapituloSiniestro(informe.capitulo_descripcion_siniestro || '');
            setCapituloRiesgo(informe.capitulo_riesgo_asegurado || '');
            setCapituloCapitales(informe.capitulo_calculo_capitales || '');
            setTablaValoracion(informe.tabla_valoracion || []);

            if ((informe as any).estado_ia === 'GENERANDO') {
                setGenerating(true);
            }
            prvCargarTemplates();
        }
    }, [informe]);

    const prvCargarTemplates = async () => {
        if (!informe) return;
        try {
            const templatesRes = await informeService.obtenerTemplates();
            setTemplates(templatesRes.data);
        } catch (err) {
            console.error('Error al cargar plantillas:', err);
        }
    };

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (generating && informe) {
            interval = setInterval(async () => {
                try {
                    const response = await informeService.getEstadoGeneracionIA(informe.id);
                    if (response.data.estado_ia === 'COMPLETADO') {
                        setGenerating(false);
                        const refreshRes = await informeService.obtenerInforme(informe.id);
                        onUpdate(refreshRes.data);
                    } else if (response.data.estado_ia === 'ERROR') {
                        setGenerating(false);
                        setError('Ocurrió un error al generar el informe en segundo plano.');
                    }
                } catch (err: any) {
                    console.error('Error polling status:', err);
                    setError(err.response?.data?.error || 'Error al obtener el estado de generación.');
                }
            }, 3000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [generating, informe, onUpdate]);

    const prvHandleGenerateTodo = async () => {
        if (!informe) return;
        setGenerating(true);
        setError('');
        try {
            const response = await informeService.generarInformeIA(informe.id);
            if (response.data.success && response.data.status === 'procesando') {
                setGenerating(true);
            } else if (response.data.success && response.data.informe) {
                onUpdate(response.data.informe);
                setGenerating(false);
            }
        } catch (err: any) {
            console.error('Error al generar informe:', err);
            setError(err.response?.data?.error || 'Error al iniciar la generación del informe por capítulos');
            setGenerating(false);
        }
    };

    const prvHandleRegenerarCapitulo = async (capituloKey: string) => {
        if (!informe) return;
        setLoadingCapitulo(capituloKey);
        setError('');
        try {
            const response = await informeService.generarCapituloIA(informe.id, capituloKey);
            onUpdate(response.data);
        } catch (err: any) {
            console.error(`Error al regenerar capítulo ${capituloKey}:`, err);
            setError(err.response?.data?.error || `Error al regenerar el capítulo`);
        } finally {
            setLoadingCapitulo(null);
        }
    };

    const prvHandleSave = async () => {
        if (!informe) return;
        setLoading(true);
        setError('');
        try {
            const response = await informeService.actualizarInformeMarkdown(
                informe.id,
                undefined,
                tablaValoracion,
                {
                    capitulo_datos_cliente: capituloCliente,
                    capitulo_descripcion_siniestro: capituloSiniestro,
                    capitulo_riesgo_asegurado: capituloRiesgo,
                    capitulo_calculo_capitales: capituloCapitales,
                }
            );
            onUpdate(response.data);
            setIsEditing(false);
        } catch (err: any) {
            console.error('Error al guardar cambios:', err);
            setError(err.response?.data?.error || 'Error al guardar cambios');
        } finally {
            setLoading(false);
        }
    };

    const prvHandleDownloadDocx = async () => {
        if (!informe) return;
        setDownloadingDocx(true);
        setError('');
        try {
            const response = await informeService.descargarDocx(informe.id, selectedTemplateId);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Informe_${informe.numero_expediente}.docx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err: any) {
            console.error('Error al descargar DOCX con IA:', err);
            setError(err.response?.data?.error || 'Error al generar el documento con IA');
        } finally {
            setDownloadingDocx(false);
        }
    };

    const prvHandleSiguienteFase = async () => {
        if (!informe) return;
        let updatedInforme = informe;
        try {
            const res = await informeService.cambiarFase(informe.id, 'envio');
            updatedInforme = res.data;
        } catch (e) {
            console.error('Error al cambiar de fase:', e);
        }
        onUpdate(updatedInforme, 'envio');
    };

    const tieneContenido = Boolean(capituloCliente || capituloSiniestro || capituloRiesgo);

    return (
        <div className="encargo-form">
            <div className="form-header-section flex justify-between items-center mb-4">
                <div>
                    <h3 className="section-title">Informe Pericial por Capítulos (IA)</h3>
                </div>
                <div className="flex items-center gap-3" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {tieneContenido && !isEditing && templates.length > 0 && (
                        <select
                            value={selectedTemplateId || ''}
                            onChange={(e) => setSelectedTemplateId(e.target.value ? parseInt(e.target.value) : undefined)}
                            style={{
                                fontSize: '13px',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                color: '#334155',
                                backgroundColor: '#ffffff'
                            }}
                        >
                            <option value="">Estilo por defecto (Documento limpio)</option>
                            {templates.map(t => (
                                <option key={t.id} value={t.id}>Plantilla: {t.nombre}</option>
                            ))}
                        </select>
                    )}
                    {tieneContenido && !isEditing && (
                        <button
                            onClick={prvHandleDownloadDocx}
                            disabled={downloadingDocx}
                            className="btn btn-secondary text-sm flex items-center gap-1"
                            style={{
                                padding: '6px 14px',
                                backgroundColor: '#f1f5f9',
                                color: '#334155',
                                border: '1px solid #cbd5e1',
                                borderRadius: '6px',
                                cursor: downloadingDocx ? 'not-allowed' : 'pointer',
                                fontWeight: 500
                            }}
                        >
                            {downloadingDocx ? (
                                <>
                                    <FiLoader className="animate-spin text-blue-600" />
                                    <span>Generando DOCX con IA...</span>
                                </>
                            ) : (
                                <>
                                    <FiFileText />
                                    <span>Descargar DOCX</span>
                                </>
                            )}
                        </button>
                    )}
                    {informe && (
                        <button
                            type="button"
                            className="btn btn-primary text-sm"
                            onClick={prvHandleSiguienteFase}
                        >
                            Siguiente Fase
                        </button>
                    )}
                </div>
            </div>

            {error && <div className="error-message mb-4">{error}</div>}

            {!tieneContenido && !generating && (
                <div className="empty-state-container text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <div className="text-4xl mb-4 text-blue-500"><FiLayers className="mx-auto" /></div>
                    <p className="text-gray-600 mb-4">Aún no se han generado los capítulos del informe pericial.</p>
                    <button
                        onClick={prvHandleGenerateTodo}
                        className="btn btn-primary"
                        disabled={generating}
                    >
                        Generar Todos los Capítulos con IA
                    </button>
                </div>
            )}

            {generating && (
                <div className="loading-state text-center py-12 flex flex-col items-center justify-center">
                    <div className="relative flex items-center justify-center mb-4">
                        <FiLoader className="animate-spin text-blue-600 text-5xl" />
                        <FiCpu className="text-blue-500 text-xl absolute" />
                    </div>
                    <p className="text-lg font-medium text-gray-700 flex items-center justify-center gap-2">
                        <FiRefreshCw className="animate-spin text-blue-600 text-xl inline-block" />
                        <span>Generando los 4 capítulos en paralelo con IA...</span>
                    </p>
                    <p className="text-sm text-gray-500 mt-2">Esto puede tardar unos segundos</p>
                </div>
            )}

            {tieneContenido && !generating && (
                <div className="form-section">
                    {/* Barra de Pestañas Navegables por Capítulo */}
                    <div className="tabs-header flex gap-2 border-b border-gray-200 mb-4 overflow-x-auto pb-1" style={{ display: 'flex', gap: '8px', borderBottom: '2px solid #e2e8f0', paddingBottom: '4px' }}>
                        {[
                            { id: 'cliente', label: '📋 1. Datos Cliente', key: 'capitulo_datos_cliente' },
                            { id: 'siniestro', label: '🔥 2. Desc. Siniestro', key: 'capitulo_descripcion_siniestro' },
                            { id: 'riesgo', label: '🏢 3. Riesgo Asegurado', key: 'capitulo_riesgo_asegurado' },
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as CapituloTab)}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '6px 6px 0 0',
                                    fontWeight: activeTab === tab.id ? 600 : 400,
                                    color: activeTab === tab.id ? '#2563eb' : '#64748b',
                                    borderBottom: activeTab === tab.id ? '3px solid #2563eb' : '3px solid transparent',
                                    backgroundColor: activeTab === tab.id ? '#eff6ff' : 'transparent',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Acciones del Capítulo Seleccionado */}
                    <div className="flex justify-between items-center mb-4">
                        <span className="font-medium text-gray-700">
                            {activeTab === 'cliente' && 'Capítulo 1: Datos del Cliente y Expediente'}
                            {activeTab === 'siniestro' && 'Capítulo 2: Descripción del Siniestro'}
                            {activeTab === 'riesgo' && 'Capítulo 3: Descripción del Riesgo Asegurado'}
                        </span>

                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    const keyMap: Record<string, string> = {
                                        cliente: 'capitulo_datos_cliente',
                                        siniestro: 'capitulo_descripcion_siniestro',
                                        riesgo: 'capitulo_riesgo_asegurado'
                                    };
                                    prvHandleRegenerarCapitulo(keyMap[activeTab]);
                                }}
                                    disabled={loadingCapitulo !== null}
                                    className="text-sm px-3 py-1 bg-blue-100 hover:bg-blue-200 rounded border border-blue-300 text-blue-700 font-medium flex items-center gap-1"
                                >
                                    {loadingCapitulo ? <FiRefreshCw className="animate-spin" /> : <FiRefreshCw />}
                                    <span>Regenerar solo este capítulo</span>
                                </button>

                            {!isEditing ? (
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="text-sm px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded border border-gray-300 text-gray-700 flex items-center gap-1"
                                >
                                    <FiEdit /> Editar
                                </button>
                            ) : (
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => {
                                            setIsEditing(false);
                                            if (informe) {
                                                setCapituloCliente(informe.capitulo_datos_cliente || '');
                                                setCapituloSiniestro(informe.capitulo_descripcion_siniestro || '');
                                                setCapituloRiesgo(informe.capitulo_riesgo_asegurado || '');
                                                setCapituloCapitales(informe.capitulo_calculo_capitales || '');
                                            }
                                        }}
                                        className="text-sm px-3 py-1 bg-white hover:bg-gray-50 rounded border border-gray-300 text-gray-700"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={prvHandleSave}
                                        disabled={loading}
                                        className="text-sm px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-white flex items-center gap-1"
                                    >
                                        <FiCheck /> {loading ? 'Guardando...' : 'Guardar Cambios'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Renderizado del Editor o Vista Previa según la Pestaña Activa */}
                    {activeTab === 'cliente' && (
                        isEditing ? (
                            <MDXEditor markdown={capituloCliente} onChange={setCapituloCliente} plugins={[headingsPlugin(), listsPlugin(), quotePlugin(), thematicBreakPlugin(), markdownShortcutPlugin(), toolbarPlugin({ toolbarContents: () => (<div style={{ display: 'flex', gap: '4px' }}><UndoRedo /><BlockTypeSelect /><BoldItalicUnderlineToggles /></div>) })]} />
                        ) : (
                            <div className="markdown-preview bg-white p-6 border border-gray-200 rounded-lg min-h-64 overflow-y-auto">
                                <ReactMarkdown>{capituloCliente}</ReactMarkdown>
                            </div>
                        )
                    )}

                    {activeTab === 'siniestro' && (
                        isEditing ? (
                            <MDXEditor markdown={capituloSiniestro} onChange={setCapituloSiniestro} plugins={[headingsPlugin(), listsPlugin(), quotePlugin(), thematicBreakPlugin(), markdownShortcutPlugin(), toolbarPlugin({ toolbarContents: () => (<div style={{ display: 'flex', gap: '4px' }}><UndoRedo /><BlockTypeSelect /><BoldItalicUnderlineToggles /></div>) })]} />
                        ) : (
                            <div className="markdown-preview bg-white p-6 border border-gray-200 rounded-lg min-h-64 overflow-y-auto">
                                <ReactMarkdown>{capituloSiniestro}</ReactMarkdown>
                            </div>
                        )
                    )}

                    {activeTab === 'riesgo' && (
                        isEditing ? (
                            <MDXEditor markdown={capituloRiesgo} onChange={setCapituloRiesgo} plugins={[headingsPlugin(), listsPlugin(), quotePlugin(), thematicBreakPlugin(), markdownShortcutPlugin(), toolbarPlugin({ toolbarContents: () => (<div style={{ display: 'flex', gap: '4px' }}><UndoRedo /><BlockTypeSelect /><BoldItalicUnderlineToggles /></div>) })]} />
                        ) : (
                            <div className="markdown-preview bg-white p-6 border border-gray-200 rounded-lg min-h-64 overflow-y-auto">
                                <ReactMarkdown>{capituloRiesgo}</ReactMarkdown>
                            </div>
                        )
                    )}
                </div>
            )}
        </div>
    );
};

export default InformeViewer;
